import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { AnyoRewardLedgerBaseGridData } from '../../../models/ledger/baseLedgerReqRes';
import {
  SessionRewardFilter,
  SessionRewardLedgerGridRequest,
  SessionRewardLedgerGridResponse,
  SessionRewardsGridMeta,
} from '../../../models/ledger/sessionLedgerReqRes';
import {
  AdminAddRewardToLedger,
  AdminMakePayoutToExpertsRequest,
} from '../../../services/rewards.service';
import { SessionRewardsService } from '../../../services/session-rewards.service';
import {
  RewardsGridComponent,
  RewardsGridConfig,
  RewardsGridPayload,
  RewardTableType,
} from '../../../shared/components/rewards-grid/rewards-grid.component';

@Component({
  selector: 'app-session-rewards',
  templateUrl: './session-rewards.component.html',
  styleUrl: './session-rewards.component.scss',
})
export class SessionRewardsComponent
  extends AnyoComponent
  implements AfterViewInit
{
  constructor(
    auth: AuthService,
    private toastService: ToastService,
    public sessionRewardsService: SessionRewardsService,
  ) {
    super(auth);
  }

  @ViewChild('rewardsGrid') rewardsGrid!: RewardsGridComponent<
    SessionRewardsGridMeta,
    SessionRewardFilter,
    SessionRewardLedgerGridRequest,
    SessionRewardLedgerGridResponse
  >;

  ngAfterViewInit(): void {
    this.auth.currentAuthStatus.subscribe((user) => {
      if (user) {
        if (this.rewardsGrid) {
          this.rewardsGrid.getAllOrPendingGridData(false);
        }
      }
    });
  }

  // Configuration for the reusable rewards grid component
  rewardsGridConfig: RewardsGridConfig = {
    showExportButton: false, // Disable old single button
    showNewExportButton: true, // Enable new format export (session details)
    showOldExportButton: true, // Enable old format export for backward compatibility
    showResetButton: true,
    showDateFilter: true,
    showConsolidateExportButton: true,
    showTabs: true,
    defaultTab: 'Complete',
    enablePayments: true, // Disable payments for session rewards
    enableAddReward: false, // Disable add reward for session rewards
    enableLedgerView: true,
    showSessionDetails: true, // Show session details in the grid
    pageSize: 10,
    pageSizeOptions: [10, 20, 50, 100],
    tableType: RewardTableType.SESSION_REWARDS,
  };

  title = 'Session Rewards Management';

  async ready(): Promise<void> {
    // Implementation for the abstract method from AnyoComponent
    // The actual data loading is now handled in ngAfterViewInit
    if (this.rewardsGrid) {
      this.rewardsGrid.getAllOrPendingGridData();
    }
  }

  // Event handlers for the reusable component
  onExport(data: RewardsGridPayload[]): void {
    // Handle export logic if needed
    console.log('Export session rewards data:', data);
  }

  onPayment(paymentData: AdminMakePayoutToExpertsRequest): void {
    this.sessionRewardsService.makePayout(paymentData).subscribe({
      next: () => {
        this.toastService.showSuccess('Payment Successful');
        this.rewardsGrid.closePaymentModal();
        this.rewardsGrid.getAllOrPendingGridData(true);
      },
      error: (e) => {
        const error = e.error as IAnyoError;
        this.toastService.showError(error.description);
      },
    });
  }

  onAddReward(rewardData: AdminAddRewardToLedger): void {
    this.sessionRewardsService.addRewardToLedger(rewardData).subscribe({
      next: () => {
        this.toastService.showSuccess('Reward Added Successfully');
        this.rewardsGrid.closeAddRewardModal();
        this.rewardsGrid.getAllOrPendingGridData(true);
      },
      error: (e) => {
        const error = e.error as IAnyoError;
        this.toastService.showError(error.description);
      },
    });
  }

  onViewLedger(
    data: AnyoRewardLedgerBaseGridData<SessionRewardsGridMeta>,
  ): void {
    // Handle ledger view logic if needed
    console.log('View session ledger for:', data);
  }

  onDataChange(
    data: AnyoRewardLedgerBaseGridData<SessionRewardsGridMeta>[],
  ): void {
    // Handle data changes if needed
    console.log('Session rewards data changed:', data);
  }

  onFilterChange(filters: any): void {
    // Handle filter changes if needed
    console.log('Session rewards filters changed:', filters);
  }
}
